import { api, type ApiResponse } from './api'
import { ROLES, type RoleName } from '@/types'

/**
 * Cliente de usuarios del CRM.
 *
 * Se llama "usuarios" y no "asesores" a propósito. El spec original hablaba de
 * una tabla `advisors` que quedó como legado huérfano: las cotizaciones
 * relacionan con `users`, y tener una segunda tabla de "asesores" sería dos
 * fuentes de verdad para lo mismo. Un usuario es un asesor con más permisos, no
 * una entidad distinta.
 */

export interface CrmUser {
  id: number
  username: string
  name: string
  email: string | null
  phone: string | null
  attention_line: string | null
  active: boolean
  roles: string[]
  permissions: string[]
  last_login_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface UserMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  query: Record<string, unknown>
}

/**
 * Payload de alta y de edición.
 *
 * `password` es obligatoria en el alta y opcional en la edición. En la edición se
 * manda explícitamente `null` para no tocarla: omitirla y mandarla a `null` no son
 * lo mismo, y quien llama tiene que poder distinguir "no la cambio" de "la borro".
 */
export interface UserPayload {
  username?: string
  name?: string
  email?: string | null
  phone?: string | null
  attention_line?: string | null
  active?: boolean
  password?: string | null
  password_confirmation?: string | null
  roles?: string[]
}

export interface UserPage {
  data: CrmUser[]
  meta: UserMeta
}

export interface UserListParams {
  search?: string
  role?: string
  /** `true` o `false`; se manda como `1`/`0` porque el backend no acepta `"true"`. */
  active?: boolean
  page?: number
  per_page?: number
}

/** Roles asignables en los formularios. */
export const ASSIGNABLE_ROLES = ROLES

export type AssignableRole = RoleName

/**
 * Descarta los filtros vacíos antes de la petición.
 *
 * Sin esto, `search: ''` llegaría al backend como parámetro presente y filtraría
 * por el string vacío en vez de devolver el listado completo.
 */
function withoutEmpty<T extends object>(params: T): Partial<T> {
  const cleaned: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key as keyof T] = value as T[keyof T]
  }
  return cleaned
}

/**
 * Normaliza los booleanos del filtro.
 *
 * `axios` serializa `true` como la cadena `"true"`, y la regla `boolean` de
 * Laravel la rechaza con un 422 que no dice nada útil. Convertir aquí deja el
 * motivo en un solo sitio en vez de en cada llamada.
 */
function normalizeBooleans(params: UserListParams): UserListParams {
  const normalized: UserListParams = { ...params }

  if (typeof params.active === 'boolean') {
    normalized.active = params.active
  }

  return normalized
}

export const usersApi = {
  list(params: UserListParams = {}) {
    return api.get<UserPage>('/users', {
      params: withoutEmpty(normalizeBooleans(params)),
    })
  },

  get(id: number) {
    return api.get<ApiResponse<CrmUser>>(`/users/${id}`)
  },

  create(payload: UserPayload) {
    return api.post<ApiResponse<CrmUser>>('/users', withoutEmpty(payload))
  },

  update(id: number, payload: UserPayload) {
    return api.patch<ApiResponse<CrmUser>>(`/users/${id}`, withoutEmpty(payload))
  },

  /**
   * Alta y baja lógica.
   *
   * Es su propio endpoint y no un `PATCH` más porque desactivar a alguien que
   * está delante de la pantalla es una decisión deliberada: además cierra sus
   * sesiones abiertas.
   */
  toggleActive(id: number) {
    return api.patch<ApiResponse<CrmUser>>(`/users/${id}/toggle-active`)
  },

  /**
   * Obtiene todas las líneas de atención únicas de los usuarios.
   *
   * Se usa en el cotizador (edición) para poblar el desplegable de líneas
   * de atención disponibles.
   */
  getAttentionLines() {
    return api.get<{ data: string[] }>('/users/attention-lines')
  },
}
